import NotificationsDirector from '#src/client/common/notifications/NotificationsDirector.jsx';
import style from '#src/client/common/notifications/Notifications.module.css';
import Items from '#src/client/common/layout/Items.jsx';
import Collapser from '#src/client/common/layout/Collapser.jsx';
import IconX from '#src/client/common/icons/IconX.jsx';
import Button from '#src/client/common/interactive/Button.jsx';
import SideBox from '#src/client/common/boxes/SideBox.jsx';
import LabeledItem from '#src/client/common/displayers/LabeledItem.jsx';
import { batch, createSignal } from 'solid-js';
import { genRequest } from '#src/client/common/utils/browser/fetchHelper.js';
import isEmpty from '#src/client/common/utils/isEmpty.js';
import cleanMetaForErrorReporting from '#src/client/common/utils/cleanMetaForErrorReporting.js';
import makeJavascriptReadable from '#src/client/common/utils/makeJavascriptReadable.js';

const [store] = NotificationsDirector.setup();

function Error(props) {
    const onToggleDetails = () => {
        props.onToggleDetailsAt(props.index);
    };

    const onRemove = () => {
        props.onDismissAt(props.index);
    };

    return (
        <Collapser isOpen={props.state.isOpen}>
            <div class={style.errorBox}>
                <Items type="horizontal" extra={['alignCenter']}>
                    <span class="stretch preWrap">{props.state.value.message}</span>
                    <Items type="horizontal" size="medium">
                        <Button onClick={onToggleDetails} color="gray" size="small">Details</Button>
                        <button onClick={onRemove} classList={{ [style.errorDismissButton]: true, [style.open]: props.state.isOpen }}>
                            <IconX />
                        </button>
                    </Items>
                </Items>
                <Collapser isOpen={props.state.areDetailsVisible}>
                    <div class={style.detailsBox}>
                        <SideBox size="small" borderColor="gray" contentColor="white">
                            <Items>
                                <Items size="medium">
                                    <For each={Object.keys(props.state.value)}>
                                        {(errorKey) => (
                                            <LabeledItem label={errorKey}>
                                                <p class={style.errorPre}>{makeJavascriptReadable(props.state.value[errorKey])}</p>
                                            </LabeledItem>
                                        )}
                                    </For>
                                </Items>
                                <Show when={!props.state.hasBeenSubmitted}>
                                    <Button onClick={[props.onSubmitErrorToLogs, props.index]} color="warning" class="flexStart">Submit Error Report</Button>
                                </Show>
                            </Items>
                        </SideBox>
                    </div>
                </Collapser>
            </div>
        </Collapser>
    );
}

const hasUnauthorizedError = (errors) => {
    return errors.some((error) => {
        return error.code === 401;
    });
};

export default function Notifications(props) {
    const [isSubmitting, setIsSubmitting] = createSignal(false);

    const hasVisibleErrors = () => {
        return store.errors.some((error) => {
            return error.isOpen;
        });
    };

    const hasSuccess = () => {
        return Boolean(store.success?.id);
    };

    const getSpacingClass = () => {
        const classes = [
            'noPrint',
            style.spacer,
        ];

        if (hasVisibleErrors()) {
            classes.push(style.spacerErrors);
        }

        if (hasSuccess()) {
            classes.push(style.spacerSuccess);
        }

        return classes.join(' ');
    };

    const onToggleDetailsAt = (index) => {
        NotificationsDirector.updateNotificationErrorAt(index, (error) => {
            error.areDetailsVisible = !error.areDetailsVisible;
        });
    };

    const onDismissAt = (index) => {
        NotificationsDirector.dismissNotificationErrorAt(index);
    };

    const successStateToStyleMap = {
        [NotificationsDirector.successStates.created]: style.created,
        [NotificationsDirector.successStates.appearing]: style.appearing,
        [NotificationsDirector.successStates.dismissing]: style.dismissing,
    };

    const successBoxClasses = () => {
        const classes = [
            'noPrint',
            style.successBox,
            !isEmpty(store.success) ? successStateToStyleMap[store.success.state] : null,
        ];

        return classes.filter(Boolean).join(' ');
    };

    const onDismissSuccess = () => {
        NotificationsDirector.dismissNotificationSuccess();
    };

    const onSubmitErrorToLogs = async (errorIndex) => {
        batch(() => {
            setIsSubmitting(true);

            NotificationsDirector.updateNotificationErrorAt(errorIndex, (error) => {
                error.hasBeenSubmitted = true;
            });
        });

        const { message, name, code, stack, details } = store.errors[errorIndex]?.value ?? {};

        let updatedStack = null;
        if (stack) {
            updatedStack = stack.replace('\\n', '\n');
        }

        const [errors] = await genRequest({
            url: '/report-error/api/',
            body: {
                url: document.location.href,
                error: {
                    message: message ?? null,
                    name: name ?? null,
                    code: code ?? null,
                    details: details ?? null,
                    stack: updatedStack ?? null,
                },
                meta: cleanMetaForErrorReporting(props.meta),
            },
        });

        batch(() => {
            if (!isEmpty(errors) && !hasUnauthorizedError(errors)) {
                // If the server returned errors, we can at least let them resubmit.
                NotificationsDirector.updateNotificationErrorAt(errorIndex, (error) => {
                    error.hasBeenSubmitted = false;
                });
            }

            setIsSubmitting(false);
        });
    };

    return (
        <>
            <div class={getSpacingClass()} />

            <Portal mount={props.renderAreaElement}>
                <fieldset disabled={isSubmitting()} class="noPrint">
                    <div classList={{ [style.errorsBox]: true, [style.open]: hasVisibleErrors() }}>
                        <For each={store.errors}>
                            {(error, index) => (
                                <Error
                                    state={error}
                                    index={index()}
                                    onDismissAt={onDismissAt}
                                    onToggleDetailsAt={onToggleDetailsAt}
                                    onSubmitErrorToLogs={onSubmitErrorToLogs} />
                            )}
                        </For>
                    </div>
                </fieldset>

                <div class={successBoxClasses()}>
                    <Show when={!isEmpty(store.success)}>
                        <Items type="horizontal" class={style.successMessageBox}>
                            <p class={style.successMessage}>{store.success.value}</p>

                            <button onClick={onDismissSuccess} classList={{ [style.successDismissButton]: true, [style.successButtonVisible]: store.success.state === NotificationsDirector.successStates.appearing }}>
                                <IconX />
                            </button>
                        </Items>
                    </Show>
                </div>
            </Portal>
        </>
    );
}
